/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AddDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButtonV2 from "../common/button/CustomButtonV2";

function AddDialog({
  disabled,
  open,
  onClose,
  title,
  componentMap,
  radioOptions,
  formLayout,
  singleColumnRows,
  handleClickAddDialog,
  onlyForLS = false,
  handleClickUpdateVersion,
  handleClickUpdateRevision,
  versionName,
  revisionName,
  titleAlign = "center",
  hideDividers = false,
}) {
  const dynamicLayout = [];
  for (let i = 0, rowNumber = 1; i < formLayout.length;) {
    if (singleColumnRows.includes(rowNumber)) {
      dynamicLayout.push([formLayout[i]]);
      i += 1;
    } else {
      dynamicLayout.push(formLayout.slice(i, i + 2));
      i += 2;
    }
    rowNumber++;
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 1.5 },
      }}
    >
      <DialogTitle
        sx={{
          position: "relative",
          textAlign: titleAlign,
          borderBottom: hideDividers ? "none" : "1px solid #e0e0e0",
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          mt: 2,
          maxHeight: "60vh",
          overflowY: "auto",
          pr: 1,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#b0b0b0",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#888",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
            borderRadius: "8px",
          },
        }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          {dynamicLayout.map((row, rowIndex) => (
            <Box
              key={row.map(f => f?.props?.name || f?.component).join("-")}
              sx={{
                display: "grid",
                gridTemplateColumns: row.length === 1 ? "1fr" : "1fr 1fr",
                gap: 2,
                width: "100%",
              }}
            >
              {row.map((field, fieldIndex) => {
                if (field?.props?.is_zonal === false) return null;
                const Component = componentMap[field.component];
                if (!Component) return null;
                return (
                  <Component key={field?.props?.name || field?.props?.id || `field-item-${fieldIndex}`} size="small" {...field.props} />
                );
              })}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: hideDividers ? "none" : "1px solid #e0e0e0",
          pt: 1,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        {!onlyForLS && (
          <>
            <CustomButtonV2
              type="cancel"
              btnName="Cancel"
              handleOnClick={onClose}
            />
            <CustomButtonV2
              disabled={disabled}
              type="submit"
              btnName={
                title?.substring(0, 6).toLowerCase() === "update"
                  ? "Update"
                  : "Add"
              }
              handleOnClick={handleClickAddDialog}
            />
          </>
        )}

        {onlyForLS && (
          <>
            <CustomButtonV2
              type="cancel"
              btnName="Cancel"
              handleOnClick={onClose}
            />
            <CustomButtonV2
              disabled={disabled}
              type="submit"
              btnName={versionName}
              handleOnClick={handleClickUpdateVersion}
            />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AddDialog;

AddDialog.propTypes = {
  titleAlign: PropTypes.any,
  hideDividers: PropTypes.any,
  onlyForLS: PropTypes.any,
  handleClickUpdateVersion: PropTypes.any,
  handleClickUpdateRevision: PropTypes.any,
  versionName: PropTypes.any,
  revisionName: PropTypes.any,
  disabled: PropTypes.any,
  open: PropTypes.any,
  onClose: PropTypes.any,
  title: PropTypes.any,
  componentMap: PropTypes.any,
  radioOptions: PropTypes.any,
  formLayout: PropTypes.any,
  singleColumnRows: PropTypes.any,
  handleClickAddDialog: PropTypes.any,
};
